/**
 * What the view's archive is cut to. A full-archive view plays any moment its cameras recorded,
 * and live; a contained one plays only what a case or one bookmark holds, and offers no live.
 *
 * Every request to open or seek the view names its scope, and a request naming another scope
 * than the view's rebuilds the view from nothing (camera_view's Scene).
 */

import type { Bookmark } from './bookmarks';

/**
 * A frozen object rather than an enum: the scope rides the local props of the view panel, so it
 * is read back from JSON.
 */
export const ViewScopeKind = {
    FullArchive: 'full_archive',
    Case: 'case',
    Bookmark: 'bookmark',
} as const;

export type ViewScopeKind = (typeof ViewScopeKind)[keyof typeof ViewScopeKind];

export type ViewScope =
    | { kind: typeof ViewScopeKind.FullArchive } |
    { kind: typeof ViewScopeKind.Case; caseId: string } |
    /** The bookmark as it was opened: the bookmarks API reads them by listing, not by id. */
    { kind: typeof ViewScopeKind.Bookmark; bookmark: Bookmark };

export type ContainedKind = Exclude<ViewScopeKind, typeof ViewScopeKind.FullArchive>;

/** A contained scope, resolved to what the view plays. */
export interface Containment {
    kind: ContainedKind;
    /** The case's title, or what the bookmark says - which may be nothing. */
    title: string;
    /** The case's bookmarks, or the one bookmark: the spans the view may play. */
    bookmarks: readonly Bookmark[];
}

/** The bookmark the view is cut to, as the latest read of it found it. */
export interface ViewBookmarkSnapshot {
    /** The bookmark the read asked for; null while the view is cut to none. */
    id: string | null;
    /** What the read found; null once it no longer finds the bookmark. */
    bookmark: Bookmark | null;
    isLoading: boolean;
    error: Error | null;
}
